import { MessageChat } from '../types';
import { getPreviewContentMessageChat } from './chat';

type Translation = (key: string, options?: Record<string, unknown>) => string;

export type ChatListMessageStatus = 'none' | 'sent' | 'delivered' | 'read';

export interface ChatListPreview {
    text: string;
    status: ChatListMessageStatus;
}

/**
 * Builds the compact preview shown below a conversation in the chat list.
 * System messages are notifications, not messages authored by the logged-in
 * user, so they never get a sender prefix or a delivery receipt.
 */
export function getChatListPreview(
    message: MessageChat | null | undefined,
    myUserId: number | string | undefined,
    isGroup: boolean,
    t: Translation,
    directSenderName = '',
): ChatListPreview {
    if (!message) return { text: '', status: 'none' };

    const isSystemMessage = message.message_type === 'system';
    const isOwnMessage = myUserId !== undefined && Number(message.user_id) === Number(myUserId);
    const senderName = (
        message.user?.name?.trim() || message.user?.user_name?.trim() || ''
    ).split(' ')[0];
    const senderPrefix = isSystemMessage
        ? ''
        : isOwnMessage
        ? ''
        : isGroup && senderName
        ? `${senderName}: `
        : !isGroup && directSenderName.trim()
        ? `${directSenderName.trim().split(' ')[0]}: `
        : '';

    let status: ChatListMessageStatus = 'none';
    if (isOwnMessage && !isSystemMessage && !message.errorSend) {
        // In a group, the accent color means that every eligible participant
        // has seen the message. A lone `is_read` flag is not enough because a
        // realtime event can represent just one reader (and old cached
        // previews may contain that flag).
        const readByCount = Number(message.read_by_count);
        const readByTotal = Number(message.read_by_total);
        const readByEveryone = isGroup
            ? Number.isFinite(readByCount)
                && Number.isFinite(readByTotal)
                && readByTotal > 0
                && readByCount >= readByTotal
            : Boolean(message.is_read);

        // Keep the same semantics used by the conversation view: a local
        // optimistic message has one check, a server-confirmed message has
        // two, and read messages use the brown accent color in the UI.
        status = message.idNotSend ? 'sent' : readByEveryone ? 'read' : 'delivered';
    }

    const content = isSystemMessage && message.system_key
        ? t(message.system_key, {
            ...(message.system_params ?? {}),
            actor: isOwnMessage
                ? t('chat.you', { defaultValue: 'Você' })
                : (message.user?.name ?? t('chat.unknown', { defaultValue: 'Usuário' })),
        })
        : getPreviewContentMessageChat(message);

    return {
        text: senderPrefix + content,
        status,
    };
}
